import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DateService } from '@lemon/core/service/date.service'
import {
  ItTyopoytaHaeMobiilikirjautumisetPyynto,
  ItTyopoytaHaeMobiilikirjautumisetVastaus,
  Mobiilialusta,
  MobiilialustanKayttajat,
  MobiilikirjautumisetJakauma,
} from '@lemon/lemonaid-lemonator-functions-and-lemonator-angular/model/mobiilikirjautumiset'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { ArcElement, BarController, BarElement, CategoryScale, Chart, DoughnutController, Legend, LinearScale, Tooltip } from 'chart.js'

Chart.register(DoughnutController, ArcElement, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

interface AlustanTiedot {
  nimi: string
  kayttajat: MobiilialustanKayttajat
  laitteet: MobiilikirjautumisetJakauma[]
  kayttojarjestelmat: MobiilikirjautumisetJakauma[]
}

const VARIT = {
  iosVanha: '#9cc3f5',
  iosFlutter: '#1e6fd9',
  androidVanha: '#a6dcb4',
  androidFlutter: '#2e9d4f',
}
const NAYTETTAVIA_RIVEJA = 15

@Component({
  selector: 'app-it-mobiilisovelluksien-tilastot',
  templateUrl: './mobiilisovelluksien-tilastot.component.html',
  styleUrls: ['./mobiilisovelluksien-tilastot.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MobiilisovelluksienTilastotComponent implements OnInit, OnDestroy {
  @ViewChild('iosCanvas') iosCanvas: ElementRef<HTMLCanvasElement>
  @ViewChild('androidCanvas') androidCanvas: ElementRef<HTMLCanvasElement>
  @ViewChild('viikotCanvas') viikotCanvas: ElementRef<HTMLCanvasElement>
  @ViewChild('versiotCanvas') versiotCanvas: ElementRef<HTMLCanvasElement>

  readonly lataa = signal(false)
  readonly vastaus = signal<ItTyopoytaHaeMobiilikirjautumisetVastaus | null>(null)
  readonly alustat = signal<AlustanTiedot[]>([])

  private _kaaviot: Chart[] = []
  private _tuhottu = false

  constructor(
    private _firebaseLemonator: FirebaseLemonator,
    private _dateService: DateService,
    private _snackbar: MatSnackBar,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {}

  async ngOnInit() {
    this.lataa.set(true)
    try {
      const vastaus = await this._firebaseLemonator.functionsCall<ItTyopoytaHaeMobiilikirjautumisetPyynto, ItTyopoytaHaeMobiilikirjautumisetVastaus>('itTyopoytaHaeMobiilikirjautumiset', {})
      if (!vastaus || vastaus.e) {
        console.error('Mobiilikirjautumisten haku epäonnistui:', vastaus?.e)
        this._snackbar.open('Mobiilikirjautumisten haku epäonnistui', 'OK')
        return
      }
      this.vastaus.set(vastaus)
      this.alustat.set(
        (['i', 'a'] as Mobiilialusta[]).map(alusta => ({
          nimi: alusta === 'i' ? 'iOS' : 'Android',
          kayttajat: vastaus.alustat.find(rivi => rivi.alusta === alusta),
          laitteet: vastaus.laitteet.filter(rivi => rivi.alusta === alusta).slice(0, NAYTETTAVIA_RIVEJA),
          kayttojarjestelmat: vastaus.kayttojarjestelmat.filter(rivi => rivi.alusta === alusta).slice(0, NAYTETTAVIA_RIVEJA),
        })),
      )
    } catch (err) {
      console.error('Mobiilikirjautumisten haku heitti:', err)
      this._snackbar.open('Mobiilikirjautumisten haku heitti virheen', 'OK')
    } finally {
      this.lataa.set(false)
    }

    if (this.vastaus() && !this._tuhottu) {
      this._changeDetectorRef.detectChanges()
      this._piirraKaaviot(this.vastaus())
    }
  }

  muotoilePaiva(iso: string): string {
    return this._dateService.muotoilePaivaDate(this._dateService.parsiIso8601(iso), 'fi')
  }

  muotoilePaivaJaAika(iso: string): string {
    return this._dateService.muotoilePaivaJaAikaDate(this._dateService.parsiIso8601(iso), 'fi')
  }

  osuus(maara: number, kokonaismaara: number): string {
    if (!kokonaismaara) {
      return '0 %'
    }
    return `${Math.round((maara / kokonaismaara) * 100)} %`
  }

  private _piirraKaaviot(vastaus: ItTyopoytaHaeMobiilikirjautumisetVastaus) {
    this._tuhoaKaaviot()

    const ios = vastaus.alustat.find(rivi => rivi.alusta === 'i')
    const android = vastaus.alustat.find(rivi => rivi.alusta === 'a')
    this._kaaviot.push(this._luoDonitsi(this.iosCanvas, ios, VARIT.iosFlutter, VARIT.iosVanha))
    this._kaaviot.push(this._luoDonitsi(this.androidCanvas, android, VARIT.androidFlutter, VARIT.androidVanha))

    this._kaaviot.push(
      new Chart(this.viikotCanvas.nativeElement, {
        type: 'bar',
        data: {
          labels: vastaus.viikot.map(viikko => this.muotoilePaiva(viikko.viikkoAlkaaIso)),
          datasets: [
            { label: 'iOS, vanha', data: vastaus.viikot.map(viikko => viikko.iosVanha), backgroundColor: VARIT.iosVanha, stack: 'ios' },
            { label: 'iOS, Flutter', data: vastaus.viikot.map(viikko => viikko.iosFlutter), backgroundColor: VARIT.iosFlutter, stack: 'ios' },
            { label: 'Android, vanha', data: vastaus.viikot.map(viikko => viikko.androidVanha), backgroundColor: VARIT.androidVanha, stack: 'android' },
            { label: 'Android, Flutter', data: vastaus.viikot.map(viikko => viikko.androidFlutter), backgroundColor: VARIT.androidFlutter, stack: 'android' },
          ],
        },
        options: {
          maintainAspectRatio: false,
          scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } } },
        },
      }),
    )

    const versiot = vastaus.versiot.slice(0, NAYTETTAVIA_RIVEJA)
    this._kaaviot.push(
      new Chart(this.versiotCanvas.nativeElement, {
        type: 'bar',
        data: {
          labels: versiot.map(rivi => `${rivi.alusta === 'i' ? 'iOS' : 'Android'} ${rivi.arvo}`),
          datasets: [
            {
              label: 'Käyttäjiä',
              data: versiot.map(rivi => rivi.kayttajia),
              backgroundColor: versiot.map(rivi => (rivi.alusta === 'i' ? VARIT.iosVanha : VARIT.androidVanha)),
            },
          ],
        },
        options: {
          indexAxis: 'y',
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
        },
      }),
    )
  }

  private _luoDonitsi(canvas: ElementRef<HTMLCanvasElement>, kayttajat: MobiilialustanKayttajat, flutterVari: string, vanhaVari: string) {
    return new Chart(canvas.nativeElement, {
      type: 'doughnut',
      data: {
        labels: ['Flutter', 'Vanha'],
        datasets: [{ data: [kayttajat.flutter, kayttajat.vanha], backgroundColor: [flutterVari, vanhaVari] }],
      },
      options: {
        maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom' } },
      },
    })
  }

  private _tuhoaKaaviot() {
    for (const kaavio of this._kaaviot) {
      kaavio.destroy()
    }
    this._kaaviot = []
  }

  ngOnDestroy() {
    this._tuhottu = true
    this._tuhoaKaaviot()
  }
}
